import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { B2BCompany } from 'npx-family-happy-common';
import { Observable, Subject, debounceTime, distinctUntilChanged, startWith, switchMap, takeUntil } from 'rxjs';
import { B2BCompanyCatalogService } from '../b2b-company-catalog.service';
import { AREAS, CUSTOMER_TYPES, CatalogFHService, LINE_OPTIONS, LINES, errorMessage, lineOf } from '../catalog.model';

type Mode = 'create' | 'link';

/** Crea, modifica o collega all'azienda un servizio esistente. */
@Component({
  selector: 'app-catalog-service-modal',
  templateUrl: './catalog-service-modal.component.html',
  styleUrls: ['./catalog-service-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
})
export class CatalogServiceModalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) company!: B2BCompany;
  /** Valorizzato = modifica. */
  @Input() service: CatalogFHService | null = null;
  /** Servizi già collegati all'azienda: nella ricerca non sono selezionabili. */
  @Input() linkedIds: number[] = [];
  @Output() saved = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();

  readonly lineOptions = LINE_OPTIONS;
  readonly lines = LINES;
  readonly areas = AREAS;
  readonly customerTypes = CUSTOMER_TYPES;
  readonly lineOf = lineOf;

  mode: Mode = 'create';
  saving = false;
  formInvalid = false;
  errorText = '';

  form = new UntypedFormGroup({
    name: new UntypedFormControl('', [Validators.required]),
    businessLine: new UntypedFormControl(null, [Validators.required]),
    customerType: new UntypedFormControl('B2B', [Validators.required]),
    active: new UntypedFormControl(true),
    script: new UntypedFormControl(''),
  });

  search = new UntypedFormControl('');
  results: CatalogFHService[] = [];
  searching = false;
  selected: CatalogFHService | null = null;
  /** Dettaglio del selezionato, con l'eventuale azienda a cui è già collegato. */
  selectedDetail: CatalogFHService | null = null;

  private unsubscribe = new Subject<void>();

  constructor(private catalog: B2BCompanyCatalogService) {}

  get isEdit() {
    return !!this.service;
  }

  get otherCompanyName(): string | null {
    const other = this.selectedDetail?.b2bCompany;
    return other && other.id !== this.company.id ? other.name || other.id : null;
  }

  ngOnInit(): void {
    if (this.service) {
      this.form.patchValue({
        name: this.service.name,
        businessLine: this.service.businessLine,
        customerType: this.service.customerType,
        active: !this.service.disabled,
        script: this.service.script ?? '',
      });
    }
    this.search.valueChanges
      .pipe(
        startWith(''),
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((text: string) => {
          this.searching = true;
          return this.catalog.searchServices(text.trim());
        }),
        takeUntil(this.unsubscribe)
      )
      .subscribe({
        next: (services) => {
          this.results = services;
          this.searching = false;
        },
        error: () => (this.searching = false),
      });
  }

  setMode(mode: Mode) {
    this.mode = mode;
    this.errorText = '';
  }

  isLinked(service: CatalogFHService) {
    return this.linkedIds.includes(service.id);
  }

  select(service: CatalogFHService) {
    this.selected = service;
    this.selectedDetail = null;
    this.catalog
      .getService(service.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((detail) => {
        if (this.selected?.id === detail.id) this.selectedDetail = detail;
      });
  }

  submit() {
    this.errorText = '';
    if (this.mode === 'link' && !this.isEdit) {
      if (!this.selected) {
        this.errorText = 'Seleziona il servizio da collegare.';
        return;
      }
      this.run(this.catalog.saveService(this.selectedDetail ?? this.selected, this.company), 'Servizio collegato');
      return;
    }
    if (this.form.invalid) {
      this.formInvalid = true;
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.value;
    const service: Partial<CatalogFHService> = {
      ...(this.service ?? {}),
      name: value.name.trim(),
      businessLine: value.businessLine,
      customerType: value.customerType,
      disabled: !value.active,
      script: this.normalizeScript(value.script),
    };
    this.run(this.catalog.saveService(service, this.company), this.isEdit ? 'Servizio aggiornato' : 'Servizio creato');
  }

  private run(request: Observable<unknown>, message: string) {
    this.saving = true;
    request.pipe(takeUntil(this.unsubscribe)).subscribe({
      next: () => {
        this.saving = false;
        this.saved.emit(message);
      },
      error: (error) => {
        this.saving = false;
        this.errorText = errorMessage(error, 'Impossibile salvare il servizio.');
      },
    });
  }

  /** Come la scheda servizio: uno script fatto solo di tag vuoti viene salvato vuoto. */
  private normalizeScript(script: string | null | undefined): string {
    if (!script) return '';
    const stripped = script.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return stripped.length === 0 ? '' : script;
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
